import { createVNode, defineAsyncComponent, getCurrentInstance, h, nextTick, ref, render, watch } from 'vue';
import type { Component } from 'vue';
import { getAppInstance } from '@/utils/app-context';

type RawSlots = {
  [name: string]: unknown;
  $stable?: boolean;
};
interface IModalOptions {
  visibleField?: string;
  component: Component | any;
  appendTo?: HTMLElement | string;
  slots?: RawSlots;
  [name: string]: unknown;
}

const getAppendToElement = (appendTo: IModalOptions['appendTo']): HTMLElement => {
  let appendToEL: HTMLElement | null = document.body;
  if (appendTo) {
    if (typeof appendTo === 'string') {
      appendToEL = document.querySelector<HTMLElement>(appendTo);
    }
    if (appendTo instanceof HTMLElement) {
      appendToEL = appendTo;
    }
    if (!(appendToEL instanceof HTMLElement)) {
      appendToEL = document.body;
    }
  }
  return appendToEL;
};

function getProvides(instance: any) {
  let provides = instance?.provides || {};
  if (instance?.parent) {
    provides = { ...provides, ...getProvides(instance.parent) };
  }
  return provides;
}

export default function useShowModal() {
  const currentInstance = getAppInstance() ?? (getCurrentInstance() as any);
  const provides = getProvides(currentInstance);

  function showModal(options: IModalOptions) {
    const visibleField = options.visibleField || 'show';
    const container = document.createElement('div');
    const isAsync = typeof options.component === 'function';
    const innerRef = ref();

    const modalComponent = isAsync ? defineAsyncComponent(options.component) : options.component;

    const props: Record<string, any> = {};
    for (const key in options) {
      if (!['component', 'appendTo', 'slots'].includes(key)) props[key] = options[key];
    }

    const vNode = createVNode({
      setup() {
        const instance = getCurrentInstance() as any;
        if (instance) {
          instance.provides = { ...instance.provides, ...provides };
          // 使用根应用实例的上下文
          instance.appContext = currentInstance.appContext;
        }
      },
      render: () =>
        h(
          modalComponent,
          {
            // visible: true,
            [visibleField]: true,
            ...props,
            ref: innerRef,
            [`onUpdate:${visibleField}`]: () => {
              nextTick(() => {
                // window.setTimeout(close, 500);
                close();
              });
            }
          },
          options.slots
        )
    });

    render(vNode, container);
    getAppendToElement(options.appendTo).appendChild(container);

    function close() {
      render(null, container);
      container.parentNode?.removeChild(container);
    }

    if (!isAsync) {
      return innerRef.value;
    }
    return new Promise(resolve => {
      watch(
        innerRef,
        () => {
          resolve(innerRef.value);
        },
        {
          once: true
        }
      );
    });
  }

  return showModal;
}
